/**
 * Investigate run contracts (factory investigate run plan, U1 / KTD5–KTD8).
 *
 * An investigate run takes a customer report, an alert or a Linear ticket and
 * returns a diagnosis with cited evidence and a ticket decision. These schemas
 * are the one shape the `tooling/investigate` package, the coordination store
 * and later callers share:
 *
 *   - {@link InvestigateInputSchema}: the input envelope (`envelope.json` of the bundle).
 *   - {@link InvestigateCitationSchema}: one piece of evidence, in a closed set of forms.
 *   - {@link InvestigateResultSchema}: what the agent returns; the citation gate checks it.
 *   - {@link InvestigateRunRecordSchema}: what a run leaves behind for comparison and grading.
 *
 * The schema checks shape and internal consistency only (indexes in range, a
 * Linear citation where a decision needs one). Whether a citation HOLDS is the
 * gate's job: it compares each excerpt with the response recorded in the run's
 * read audit, which the schema cannot see.
 */
import { z } from 'zod'
import { ResearchPacketSchema } from './agent-run'

// =============================================================================
// Shared vocabulary
// =============================================================================

/** Where the report came from. Echoed into the result so a caller can route it. */
export const INVESTIGATE_CHANNELS = ['slack', 'email', 'observatory', 'linear', 'manual'] as const
export const InvestigateChannelSchema = z.enum(INVESTIGATE_CHANNELS)
export type InvestigateChannel = z.infer<typeof InvestigateChannelSchema>

/**
 * The read tools' sources (KTD3). The repository is not here: it is read with
 * the built-in file tools and cited by `path:start-end`, never by call id.
 */
export const INVESTIGATE_SOURCES = ['database', 'datadog', 'posthog', 'hubspot', 'linear', 'blackbaud'] as const
export const InvestigateSourceSchema = z.enum(INVESTIGATE_SOURCES)
export type InvestigateSource = z.infer<typeof InvestigateSourceSchema>

/** A Linear issue key such as `SCH-1288`. Kickoff and the write step check the team. */
export const LinearKeySchema = z.string().regex(/^[A-Z][A-Z0-9]*-[1-9]\d*$/, 'a Linear issue key such as SCH-123')

// =============================================================================
// Input envelope (KTD5)
// =============================================================================

/** Matches the store's `request` field, which holds the text content (KTD8). */
export const INVESTIGATE_CONTENT_MAX_CHARS = 8000
export const INVESTIGATE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
export const INVESTIGATE_ATTACHMENT_MEDIA_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'] as const

/**
 * One file in the bundle's `attachments/` folder. The entry point reads images
 * only through this list and refuses a bundle whose files do not match it, so
 * the path is held to one flat name under `attachments/`: no absolute path, no
 * `..`, no subfolder to resolve.
 */
export const InvestigateAttachmentSchema = z.object({
  path: z
    .string()
    .max(200)
    .regex(/^attachments\/[A-Za-z0-9][A-Za-z0-9._-]*$/, 'a file name directly under attachments/'),
  mediaType: z.enum(INVESTIGATE_ATTACHMENT_MEDIA_TYPES),
  bytes: z.number().int().positive().max(INVESTIGATE_ATTACHMENT_MAX_BYTES),
  sha256: z.string().regex(/^[0-9a-f]{64}$/, 'a lowercase hex sha256')
})
export type InvestigateAttachment = z.infer<typeof InvestigateAttachmentSchema>

/** The content when it is exactly one Linear key (surrounding whitespace aside), else null. */
function contentAsLinearKey(content: string): string | null {
  const trimmed = content.trim()
  return LinearKeySchema.safeParse(trimmed).success ? trimmed : null
}

/**
 * The input envelope. GUIDANCE AND CONTENT ARE SEPARATE ON PURPOSE (R19): the
 * entry point puts guidance in the system prompt as instructions and content in
 * the user turn as fenced, untrusted data. Merging them would let a customer's
 * email instruct the agent.
 *
 * A Linear ticket key may be the whole content, so the text may be empty when a
 * ticket key or an attachment carries the report.
 *
 * ONE PLACE FOR A TICKET (R1): content that is exactly a Linear key is moved into
 * `ticketKey` on parse, so every reader of a parsed envelope finds the ticket
 * there. An envelope whose content is one key while `ticketKey` names another is
 * refused, because either reading could be the caller's intent.
 */
export const InvestigateInputSchema = z
  .object({
    channel: InvestigateChannelSchema,
    /** The caller's pointer back to the source: a Slack permalink, a HubSpot email id, an alert id. */
    sourceReference: z.string().min(1).max(500),
    content: z.string().max(INVESTIGATE_CONTENT_MAX_CHARS),
    attachments: z
      .array(InvestigateAttachmentSchema)
      .max(10)
      .refine(list => new Set(list.map(a => a.path)).size === list.length, {
        message: 'each attachment path at most once'
      }),
    guidance: z.string().min(1).max(2000).optional(),
    ticketKey: LinearKeySchema.optional()
  })
  .refine(input => input.content.trim().length > 0 || input.ticketKey !== undefined || input.attachments.length > 0, {
    message: 'an envelope needs content, an attachment or a ticket key',
    path: ['content']
  })
  .refine(
    input => {
      const key = contentAsLinearKey(input.content)
      return key === null || input.ticketKey === undefined || key === input.ticketKey
    },
    { message: 'the content is one Linear key and ticketKey names a different one', path: ['ticketKey'] }
  )
  .transform(input => {
    const key = contentAsLinearKey(input.content)
    return key === null ? input : { ...input, content: '', ticketKey: key }
  })
export type InvestigateInput = z.infer<typeof InvestigateInputSchema>

// =============================================================================
// Citations (KTD6)
// =============================================================================

/**
 * Bounds WIDENED from {@link ResearchPacketSchema}'s evidence (10 citations,
 * 160-character excerpts). A research packet rides one 12 KB phase event; an
 * investigate result is stored as its own record, and a quoted JSON row needs
 * more than 160 characters to carry the fields a claim rests on.
 */
export const INVESTIGATE_EVIDENCE_MAX = 25
export const INVESTIGATE_EXCERPT_MAX_CHARS = 400
/**
 * An excerpt this short could match almost any response, so a bare number
 * cannot hold a claim (KTD6). The gate uses the same constant.
 */
export const INVESTIGATE_EXCERPT_MIN_CHARS = 12

const ExcerptSchema = z
  .string()
  .min(INVESTIGATE_EXCERPT_MIN_CHARS)
  .max(INVESTIGATE_EXCERPT_MAX_CHARS)
  // Padding with whitespace would pass the length check and match anything once the gate collapses it.
  .refine(text => text.trim().length >= INVESTIGATE_EXCERPT_MIN_CHARS, {
    message: `an excerpt must quote at least ${INVESTIGATE_EXCERPT_MIN_CHARS} characters`
  })

/** Reused as-is from the research packet: what this citation shows. */
const CitationClaimSchema = ResearchPacketSchema.shape.evidence.element.shape.claim

/**
 * The identifier kinds the duplicate rule compares (KTD6). A duplicate target
 * counts only when its Linear citation shares an identifier of the same kind
 * and value with a held cause citation.
 */
export const INVESTIGATE_IDENTIFIER_KINDS = [
  'school-id',
  'school-slug',
  'group-id',
  'section-id',
  'job-id',
  'error-signature'
] as const
/** Shorter values (`id`, `42`) are generic enough to tie a duplicate to unrelated evidence. */
export const INVESTIGATE_IDENTIFIER_MIN_CHARS = 3
export const InvestigateIdentifierSchema = z.object({
  kind: z.enum(INVESTIGATE_IDENTIFIER_KINDS),
  value: z.string().min(INVESTIGATE_IDENTIFIER_MIN_CHARS).max(200)
})
export type InvestigateIdentifier = z.infer<typeof InvestigateIdentifierSchema>

/** A read made during the run, named by the call id its adapter recorded in the read audit. */
const ToolCitationSchema = z.object({
  kind: z.literal('tool'),
  /** Which read tool made the call; the gate checks it against the read audit. */
  source: InvestigateSourceSchema,
  callId: z.string().min(1).max(100),
  excerpt: ExcerptSchema,
  claim: CitationClaimSchema,
  identifiers: z.array(InvestigateIdentifierSchema).max(10)
})

const REPO_LOCATION = /^(?![/\\])([^\s:\\]+):([1-9]\d*)-([1-9]\d*)$/

/** Repository lines at the run's base SHA, as `path:start-end`. */
const RepoCitationSchema = z.object({
  kind: z.literal('repo'),
  location: z
    .string()
    .max(300)
    .regex(REPO_LOCATION, 'a relative path:start-end')
    .refine(location => !location.split(':')[0]!.split('/').includes('..'), { message: 'a path inside the repo' })
    .refine(
      location => {
        const [, , start, end] = REPO_LOCATION.exec(location) ?? []
        return Number(start) <= Number(end)
      },
      { message: 'a range whose start is not after its end' }
    ),
  claim: CitationClaimSchema,
  /** Optional quote from the cited lines; when given, the gate checks it is there. */
  excerpt: ExcerptSchema.optional()
})

/**
 * A closed set: a tool read or repository lines. Free text, URLs and shell
 * commands have no form here, so they cannot be cited at all.
 */
export const InvestigateCitationSchema = z.discriminatedUnion('kind', [ToolCitationSchema, RepoCitationSchema])
export type InvestigateCitation = z.infer<typeof InvestigateCitationSchema>

// =============================================================================
// Result (KTD7)
// =============================================================================

/**
 * Version 2 adds `route` (workflow engine plan KTD12). A version 1 result still
 * parses, through {@link InvestigateResultV1Schema} or
 * {@link InvestigateAnyResultSchema}, so older run records keep
 * working; a v1 result has no route, and a caller that needs one refuses it
 * rather than guessing.
 */
export const INVESTIGATE_RESULT_SCHEMA_VERSION = 2
export const INVESTIGATE_RESULT_V1_SCHEMA_VERSION = 1

/** A zero-based index into the result's `evidence` list. */
const CitationIndexSchema = z.number().int().nonnegative()

/** One statement of the cause and the citations that back it. */
export const InvestigateCauseClaimSchema = z.object({
  claim: z.string().min(1).max(500),
  citations: z.array(CitationIndexSchema).min(1).max(INVESTIGATE_EVIDENCE_MAX)
})
export type InvestigateCauseClaim = z.infer<typeof InvestigateCauseClaimSchema>

/**
 * The agent's PROPOSAL; a code step carries it out (KTD10), and the run record's
 * `ticketAction` says what actually happened.
 *
 * - `new` names the Linear search citation that shows it looked for an existing ticket.
 * - `duplicate` names the Linear citation of its target.
 * - `comment` targets the ticket the envelope gave.
 */
export const InvestigateTicketDecisionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('new'), linearSearchCitation: CitationIndexSchema }),
  z.object({ kind: z.literal('duplicate'), ticketKey: LinearKeySchema, linearCitation: CitationIndexSchema }),
  z.object({ kind: z.literal('comment'), ticketKey: LinearKeySchema })
])
export type InvestigateTicketDecision = z.infer<typeof InvestigateTicketDecisionSchema>

/**
 * What should happen next (workflow engine plan KTD12): `fix` hands the ticket to
 * a fix run once a person approves, `not_a_bug` closes the question, and
 * `needs_person` hands it to a person. The bug-to-fix judge sends `fix` with low
 * confidence to `needs_person`.
 */
export const INVESTIGATE_ROUTES = ['fix', 'not_a_bug', 'needs_person'] as const
export const InvestigateRouteSchema = z.enum(INVESTIGATE_ROUTES)
export type InvestigateRoute = z.infer<typeof InvestigateRouteSchema>

const isLinearCitation = (evidence: readonly InvestigateCitation[], index: number) => {
  const citation = evidence[index]
  return citation?.kind === 'tool' && citation.source === 'linear'
}

/** The fields every version carries. */
const resultFields = {
  skillVersion: z.string().min(1).max(100),
  /** Echoed from the envelope so a caller can act without the transcript (R11). */
  channel: InvestigateChannelSchema,
  sourceReference: z.string().min(1).max(500),
  inputSummary: ResearchPacketSchema.shape.question,
  /** The ticket title the write step uses when it creates one. */
  title: z.string().min(1).max(120),
  cause: z.array(InvestigateCauseClaimSchema).min(1).max(10),
  evidence: z.array(InvestigateCitationSchema).min(1).max(INVESTIGATE_EVIDENCE_MAX),
  /** A proposal, so it needs no citation (KTD6). */
  proposedFix: ResearchPacketSchema.shape.finding,
  confidence: ResearchPacketSchema.shape.confidence,
  ticketDecision: InvestigateTicketDecisionSchema,
  /** Optional sources that could not be read; a missing required source fails the run instead. */
  unavailableSources: z
    .array(InvestigateSourceSchema)
    .max(INVESTIGATE_SOURCES.length)
    .refine(list => new Set(list).size === list.length, { message: 'each source at most once' })
}
const ResultFieldsSchema = z.object(resultFields)

function checkResult(result: z.infer<typeof ResultFieldsSchema>, ctx: z.RefinementCtx) {
  result.cause.forEach((claim, claimIndex) => {
    claim.citations.forEach((index, position) => {
      if (index >= result.evidence.length) {
        ctx.addIssue({
          code: 'custom',
          message: `cause claim ${claimIndex} cites evidence ${index}, which does not exist`,
          path: ['cause', claimIndex, 'citations', position]
        })
      }
    })
  })
  const decision = result.ticketDecision
  if (decision.kind === 'new' && !isLinearCitation(result.evidence, decision.linearSearchCitation)) {
    ctx.addIssue({
      code: 'custom',
      message: 'a "new" decision must cite a Linear search',
      path: ['ticketDecision', 'linearSearchCitation']
    })
  }
  if (decision.kind === 'duplicate' && !isLinearCitation(result.evidence, decision.linearCitation)) {
    ctx.addIssue({
      code: 'custom',
      message: 'a "duplicate" decision must cite its target in Linear',
      path: ['ticketDecision', 'linearCitation']
    })
  }
}

/** The current result: version 2, with its route. */
export const InvestigateResultSchema = z
  .object({
    schemaVersion: z.literal(INVESTIGATE_RESULT_SCHEMA_VERSION),
    ...resultFields,
    route: InvestigateRouteSchema
  })
  .superRefine(checkResult)
export type InvestigateResult = z.infer<typeof InvestigateResultSchema>

/**
 * Version 1, with no route: records from before the workflow engine plan's U5,
 * when `tooling/investigate` moved to version 2.
 */
export const InvestigateResultV1Schema = z
  .object({ schemaVersion: z.literal(INVESTIGATE_RESULT_V1_SCHEMA_VERSION), ...resultFields })
  .superRefine(checkResult)
export type InvestigateResultV1 = z.infer<typeof InvestigateResultV1Schema>

/** Either version, told apart by `schemaVersion`: for stored records and posted results. */
export const InvestigateAnyResultSchema = z.discriminatedUnion('schemaVersion', [
  InvestigateResultV1Schema,
  InvestigateResultSchema
])
export type InvestigateAnyResult = z.infer<typeof InvestigateAnyResultSchema>

/**
 * The result as draft-07 JSON Schema, for the Agent SDK's structured output.
 * Cross-field rules (indexes in range, Linear citations) do not survive the
 * conversion; the Zod parse and the gate still enforce them. Functions rather
 * than constants so importing the package barrel does not pay for them.
 */
export function investigateResultJsonSchema() {
  return z.toJSONSchema(InvestigateResultSchema, { target: 'draft-07', io: 'input' })
}

// =============================================================================
// Run record (KTD7, KTD8)
// =============================================================================

/** The caps a run ran with (U5), stored so runs under different caps are not compared blind. */
export const InvestigateRunCapsSchema = z.object({
  maxTurns: z.number().int().positive(),
  maxToolCalls: z.number().int().positive(),
  wallClockMs: z.number().int().positive(),
  maxBudgetUsd: z.number().positive()
})
export type InvestigateRunCaps = z.infer<typeof InvestigateRunCapsSchema>

/** The citation gate's counts (KTD6). `corrections` is how many resume rounds ran (at most two). */
export const InvestigateGateCountsSchema = z.object({
  checked: z.number().int().nonnegative(),
  held: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  corrections: z.number().int().min(0).max(2)
})
export type InvestigateGateCounts = z.infer<typeof InvestigateGateCountsSchema>

/** What the write step actually did, so a caller never mistakes the proposal for a write (KTD7). */
export const INVESTIGATE_TICKET_ACTIONS = [
  'pending',
  'created',
  'commented',
  'none',
  'write-failed',
  'unknown'
] as const
export const InvestigateTicketActionSchema = z.enum(INVESTIGATE_TICKET_ACTIONS)
export type InvestigateTicketAction = z.infer<typeof InvestigateTicketActionSchema>

/**
 * One read-audit entry as the store keeps it (KTD3). The run directory's audit
 * line carries the same fields plus `response`, which only the gate reads; the
 * store never holds a response, so an entry carrying one is refused (strict).
 * The 30-day purge (KTD8) later drops `arguments` and keeps the rest.
 */
export const InvestigateReadAuditEntrySchema = z.strictObject({
  callId: z.string().min(1).max(100),
  tool: z.string().min(1).max(100),
  arguments: z.record(z.string(), z.unknown()),
  /** When the adapter's call returned. */
  observedAt: z.iso.datetime(),
  /**
   * What answered: the replica, Datadog site, PostHog project, HubSpot portal,
   * Linear workspace, or the Blackbaud product and school. Repeating the read
   * from a ticket needs it (KTD10).
   */
  sourceIdentity: z.string().min(1).max(300),
  /** Secret-shaped strings replaced in this call's arguments and response. */
  redactionCount: z.number().int().nonnegative()
})
export type InvestigateReadAuditEntry = z.infer<typeof InvestigateReadAuditEntrySchema>

/** One operator grade on the four R17 questions. Grades are append-only. */
const GradeMarkSchema = z.enum(['pass', 'partial', 'fail'])
export const InvestigateGradeSchema = z.object({
  gradedAt: z.iso.datetime(),
  cause: GradeMarkSchema,
  evidence: GradeMarkSchema,
  ticketDecision: GradeMarkSchema,
  proposedFix: GradeMarkSchema,
  note: z.string().min(1).max(1000).optional()
})
export type InvestigateGrade = z.infer<typeof InvestigateGradeSchema>

export const INVESTIGATE_RUN_TERMINAL_STATUSES = ['done', 'failed', 'cancelled'] as const

/** The Linear body length cap the write step renders to (KTD10). */
export const INVESTIGATE_RENDERED_BODY_MAX_CHARS = 20_000

/**
 * What a record-only write step would have done (KTD10, OQ4): the first factory
 * runs run the whole decision flow and record it here instead of writing Linear.
 * `create` may link an older closed duplicate; `comment` names its target.
 */
export const InvestigateRecordOnlySchema = z.object({
  intendedAction: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('create'), linksTo: LinearKeySchema.optional() }),
    z.object({ kind: z.literal('comment'), ticketKey: LinearKeySchema })
  ]),
  /** The body after cleaning and the secret scan, exactly as it would be sent. */
  renderedBody: z.string().min(1).max(INVESTIGATE_RENDERED_BODY_MAX_CHARS)
})
export type InvestigateRecordOnly = z.infer<typeof InvestigateRecordOnlySchema>

export const InvestigateRunRecordSchema = z
  .object({
    runId: z.string().min(1).max(200),
    status: z.enum(INVESTIGATE_RUN_TERMINAL_STATUSES),
    error: z.string().min(1).max(2000).optional(),
    caps: InvestigateRunCapsSchema,
    /** Absent when the run failed before producing a result. Either version: older records hold v1. */
    result: InvestigateAnyResultSchema.optional(),
    /** Absent when the run never reached the gate. */
    gate: InvestigateGateCountsSchema.optional(),
    ticketAction: InvestigateTicketActionSchema,
    /** The ticket the write step created or commented on (R11). */
    ticketKey: LinearKeySchema.optional(),
    /** Set when the write step ran record-only; the ticket action is then `none` (KTD10). */
    recordOnly: InvestigateRecordOnlySchema.optional(),
    /**
     * The commit SHA of the tool checkout that ran this (KTD12): the skill, MCP
     * server, gate and hook. `result.skillVersion` is the plugin's declared
     * version, which a change outside the plugin does not bump. Optional until
     * every runner records it.
     */
    toolSha: z
      .string()
      .regex(/^[0-9a-f]{40}$/, 'a full lowercase commit SHA')
      .optional(),
    readAudit: z.array(InvestigateReadAuditEntrySchema).max(1000),
    /** How many secret-shaped strings were replaced in responses and the result (KTD3, KTD8). */
    redactions: z.number().int().nonnegative(),
    grades: z.array(InvestigateGradeSchema)
  })
  .superRefine((record, ctx) => {
    if (record.status === 'done' && record.result === undefined) {
      ctx.addIssue({ code: 'custom', message: 'a done run must carry its result', path: ['result'] })
    }
    if (record.status === 'failed' && record.error === undefined) {
      ctx.addIssue({ code: 'custom', message: 'a failed run must carry its error', path: ['error'] })
    }
    if (record.recordOnly && record.ticketAction !== 'none') {
      ctx.addIssue({ code: 'custom', message: 'a record-only run writes nothing to Linear', path: ['ticketAction'] })
    }
  })
export type InvestigateRunRecord = z.infer<typeof InvestigateRunRecordSchema>
